interface SurveyStorageData {
  surveyGuid: string;
  respondentGuid?: string;
  isTest?: boolean;
  data: any;
  currentPage: number;
  timestamp: number;
  expiresAt: number;
}

const SURVEY_STORAGE_PREFIX = 'compass_survey_temp_';
const EXPIRATION_DAYS = 30;

function buildStorageKey(surveyGuid: string, respondentGuid?: string, isTest?: boolean): string {
  if (isTest) {
    return respondentGuid 
      ? `${SURVEY_STORAGE_PREFIX}${surveyGuid}_${respondentGuid}_test`
      : `${SURVEY_STORAGE_PREFIX}${surveyGuid}_test`;
  }
  
  return respondentGuid
    ? `${SURVEY_STORAGE_PREFIX}${surveyGuid}_${respondentGuid}`
    : `${SURVEY_STORAGE_PREFIX}${surveyGuid}_open`;
}

export interface LoadedSurveyData {
  data: any;
  currentPage: number;
}

export const saveSurveyToLocalStorage = (
  surveyGuid: string, 
  respondentGuid: string | undefined, 
  isTest: boolean | undefined,
  data: any, 
  currentPage: number
): void => {
  try {
    const now = Date.now();
    const storageData: SurveyStorageData = {
      surveyGuid,
      respondentGuid,
      isTest,
      data,
      currentPage,
      timestamp: now,
      expiresAt: now + (EXPIRATION_DAYS * 24 * 60 * 60 * 1000)
    };
    
    const key = buildStorageKey(surveyGuid, respondentGuid, isTest);
    localStorage.setItem(key, JSON.stringify(storageData));
  } catch (error) {
    console.warn('Failed to save survey data to localStorage:', error);
  }
};

export const loadSurveyFromLocalStorage = (
  surveyGuid: string, 
  respondentGuid?: string,
  isTest?: boolean
): LoadedSurveyData | null => {
  try {
    const key = buildStorageKey(surveyGuid, respondentGuid, isTest);
    const stored = localStorage.getItem(key);
    
    if (!stored) {
      return null;
    }
    
    const storageData: SurveyStorageData = JSON.parse(stored);
    
    if (Date.now() > storageData.expiresAt) {
      localStorage.removeItem(key);
      return null;
    }
    
    return {
      data: storageData.data,
      currentPage: storageData.currentPage
    };
  } catch (error) {
    console.warn('Failed to load survey data from localStorage:', error);
    return null;
  }
};

export const clearSurveyFromLocalStorage = (
  surveyGuid: string, 
  respondentGuid?: string,
  isTest?: boolean
): void => {
  try {
    const key = buildStorageKey(surveyGuid, respondentGuid, isTest);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear survey data from localStorage:', error);
  }
};

export const clearExpiredSurveys = (): void => {
  try {
    const keys = Object.keys(localStorage);
    const surveyKeys = keys.filter(key => key.startsWith(SURVEY_STORAGE_PREFIX));
    
    surveyKeys.forEach(key => {
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          const storageData: SurveyStorageData = JSON.parse(stored);
          if (Date.now() > storageData.expiresAt) {
            localStorage.removeItem(key);
          }
        } catch (error) {
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    console.warn('Failed to clear expired surveys:', error);
  }
};
